
import React, { useState } from 'react';
import Navigation from '@/components/Navigation';
import { Card, CardContent } from '@/components/ui/card';
import { CustomersList } from '@/components/customers/CustomersList';
import { useCustomers } from '@/hooks/useCustomers';
import { useRestaurantContext } from '@/context/RestaurantContext';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Users } from 'lucide-react';
import { CustomersHeader } from '@/components/customers/CustomersHeader';
import CustomerFilters from '@/components/customers/CustomerFilters';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Pagination, 
  PaginationContent, 
  PaginationEllipsis, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from '@/components/ui/pagination';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { CustomerFilters as CustomerFiltersType } from '@/types/customer';

const CustomersPage = () => {
  const { selectedRestaurants, setSelectedRestaurants } = useRestaurantContext();
  const [showFilters, setShowFilters] = useState(false);
  
  // Use the enhanced useCustomers hook with server-side filtering and pagination
  const { 
    customers,
    searchQuery, 
    setSearchQuery, 
    loading, 
    error,
    refreshData,
    filters,
    updateFilters,
    resetFilters,
    page,
    setPage,
    totalPages,
    pageSize,
    setPageSize,
    totalCount
  } = useCustomers(selectedRestaurants);
  
  // Calculate active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    
    if (filters.registrationStatus !== 'all') count++;
    if (filters.loyaltyTier.length > 0) count++;
    if (filters.orderFrequency !== 'all') count++;
    if (filters.lastOrderDays[0] > 0 || filters.lastOrderDays[1] < 365) count++;
    if (filters.spendRange[0] > 0 || filters.spendRange[1] < 10000) count++;
    if (filters.marketingOptIn !== null) count++;
    
    return count;
  };
  
  // Handle restaurant selection
  const handleRestaurantChange = (restaurantIds: string[]) => {
    setSelectedRestaurants(restaurantIds);
    setPage(1); // Reset to first page when restaurants change
  };
  
  return (
    <Navigation>
      <div className="space-y-6">
        <CustomersHeader 
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedRestaurants={selectedRestaurants}
          handleRestaurantChange={handleRestaurantChange}
          userRestaurants={[]} // This should be populated with actual user restaurants
          toggleFilters={() => setShowFilters(!showFilters)}
          showFilters={showFilters}
          activeFilterCount={getActiveFilterCount()}
        />
        
        {/* Debug information for development */}
        {process.env.NODE_ENV === 'development' && (
          <Alert className="bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-800">Debug Information</AlertTitle>
            <AlertDescription className="text-amber-700">
              <div className="text-xs font-mono overflow-auto">
                <div>Selected Restaurants: {selectedRestaurants.join(', ') || 'none'}</div>
                <div>Customer Count: {totalCount}</div>
                <div>Loading: {loading ? 'true' : 'false'}</div>
                <div>Error: {error || 'none'}</div>
                <div>Page: {page} of {totalPages}</div>
              </div>
            </AlertDescription>
          </Alert>
        )}
        
        {/* Error message */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Error Loading Customers</AlertTitle>
            <AlertDescription>
              {error}
              <div className="mt-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={refreshData}
                  className="gap-1"
                >
                  <RefreshCw className="h-3 w-3" /> Retry
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Filters sidebar - only visible when showFilters is true */}
          {showFilters && (
            <div className="md:col-span-1">
              <CustomerFilters 
                onChange={updateFilters}
                onReset={resetFilters}
                initialFilters={filters}
              />
            </div>
          )}
          
          {/* Main content */}
          <div className={`${showFilters ? 'md:col-span-3' : 'md:col-span-4'}`}>
            <Tabs defaultValue="list" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="list">List View</TabsTrigger>
                <TabsTrigger value="card">Card View</TabsTrigger>
              </TabsList>
              
              <TabsContent value="list" className="mt-0">
                <Card>
                  <CardContent className="pt-6">
                    <CustomersList
                      customers={customers}
                      loading={loading}
                      error={error}
                    />
                    
                    {/* Pagination controls */}
                    {!loading && !error && totalPages > 1 && (
                      <div className="mt-4 flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="text-sm text-muted-foreground">
                          Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} customers
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <select 
                            className="text-sm border rounded p-1"
                            value={pageSize}
                            onChange={(e) => {
                              setPageSize(Number(e.target.value));
                              setPage(1); // Reset to first page when page size changes
                            }}
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                          
                          <Pagination>
                            <PaginationContent>
                              <PaginationItem>
                                <PaginationPrevious 
                                  href="#" 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (page > 1) setPage(page - 1);
                                  }}
                                  className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                                />
                              </PaginationItem>
                              
                              {/* First page */}
                              <PaginationItem>
                                <PaginationLink 
                                  href="#" 
                                  isActive={page === 1}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setPage(1);
                                  }}
                                >
                                  1
                                </PaginationLink>
                              </PaginationItem>
                              
                              {/* Ellipsis if needed */}
                              {page > 3 && (
                                <PaginationItem>
                                  <PaginationEllipsis />
                                </PaginationItem>
                              )}
                              
                              {/* Page before current if applicable */}
                              {page > 2 && (
                                <PaginationItem>
                                  <PaginationLink 
                                    href="#" 
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setPage(page - 1);
                                    }}
                                  >
                                    {page - 1}
                                  </PaginationLink>
                                </PaginationItem>
                              )}
                              
                              {/* Current page if not first or last */}
                              {page !== 1 && page !== totalPages && (
                                <PaginationItem>
                                  <PaginationLink 
                                    href="#" 
                                    isActive={true}
                                    onClick={(e) => e.preventDefault()}
                                  >
                                    {page}
                                  </PaginationLink>
                                </PaginationItem>
                              )}
                              
                              {/* Page after current if applicable */}
                              {page < totalPages - 1 && (
                                <PaginationItem>
                                  <PaginationLink 
                                    href="#" 
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setPage(page + 1);
                                    }}
                                  >
                                    {page + 1}
                                  </PaginationLink>
                                </PaginationItem>
                              )}
                              
                              {/* Ellipsis if needed */}
                              {page < totalPages - 2 && (
                                <PaginationItem>
                                  <PaginationEllipsis />
                                </PaginationItem>
                              )}
                              
                              {/* Last page if more than one page */}
                              {totalPages > 1 && (
                                <PaginationItem>
                                  <PaginationLink 
                                    href="#" 
                                    isActive={page === totalPages}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setPage(totalPages);
                                    }}
                                  >
                                    {totalPages}
                                  </PaginationLink>
                                </PaginationItem>
                              )}
                              
                              <PaginationItem>
                                <PaginationNext 
                                  href="#" 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (page < totalPages) setPage(page + 1);
                                  }}
                                  className={page >= totalPages ? "pointer-events-none opacity-50" : ""}
                                />
                              </PaginationItem>
                            </PaginationContent>
                          </Pagination>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="card" className="mt-0">
                <Card>
                  <CardContent className="pt-6">
                    {/* Grid view of customers as cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {loading ? (
                        // Loading placeholders for card view
                        Array(6).fill(0).map((_, i) => (
                          <div key={i} className="border rounded-lg p-4 space-y-3">
                            <div className="flex items-center space-x-3">
                              <Skeleton className="w-10 h-10 rounded-full" />
                              <div className="space-y-1 flex-1">
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-3 w-4/5" />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Skeleton className="h-3 w-full" />
                              <Skeleton className="h-3 w-2/3" />
                            </div>
                            <div className="pt-2 flex justify-between">
                              <Skeleton className="h-6 w-1/3" />
                              <Skeleton className="h-6 w-1/4" />
                            </div>
                          </div>
                        ))
                      ) : error ? (
                        <div className="col-span-full p-8 text-center">
                          <AlertTriangle className="h-10 w-10 text-destructive mx-auto mb-4" />
                          <h3 className="text-lg font-medium mb-2">Error Loading Customers</h3>
                          <p className="text-muted-foreground mb-4">{error}</p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={refreshData}
                            className="gap-1"
                          >
                            <RefreshCw className="h-3 w-3" /> Retry
                          </Button>
                        </div>
                      ) : customers.length === 0 ? (
                        <div className="col-span-full p-8 text-center">
                          <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                            <Users className="h-8 w-8 text-muted-foreground" />
                          </div>
                          <h3 className="text-lg font-medium mb-2">No Customers Found</h3>
                          <p className="text-muted-foreground mb-4">
                            Try changing your filters or selecting different restaurants.
                          </p>
                          {getActiveFilterCount() > 0 && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={resetFilters}
                              className="gap-1"
                            >
                              <RefreshCw className="h-3 w-3" /> Reset Filters
                            </Button>
                          )}
                        </div>
                      ) : (
                        customers.map(customer => (
                          <div key={customer.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-center space-x-3 mb-3">
                              <Avatar>
                                <AvatarFallback>
                                  {customer.first_name?.[0] || customer.email[0].toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <h3 className="font-medium">
                                  {customer.first_name || ''} {customer.last_name || ''}
                                </h3>
                                <p className="text-xs text-muted-foreground">{customer.email}</p>
                              </div>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Orders</span>
                                <span className="font-medium">{customer.total_orders || 0}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Spent</span>
                                <span className="font-medium">${(customer.total_spend || 0).toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Last Order</span>
                                <span className="font-medium">
                                  {customer.latest_order_date 
                                    ? new Date(customer.latest_order_date).toLocaleDateString() 
                                    : "Never"}
                                </span>
                              </div>
                            </div>
                            <div className="pt-3 flex justify-between items-center">
                              <div>
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  customer.is_registered 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {customer.is_registered ? "Registered" : "Not Registered"}
                                </span>
                              </div>
                              <Button variant="ghost" size="sm" asChild>
                                <a href={`/customers/${customer.id}`}>View</a>
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    
                    {/* Pagination for card view */}
                    {!loading && !error && customers.length > 0 && totalPages > 1 && (
                      <div className="mt-6 flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="text-sm text-muted-foreground">
                          Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} customers
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <select 
                            className="text-sm border rounded p-1"
                            value={pageSize}
                            onChange={(e) => {
                              setPageSize(Number(e.target.value));
                              setPage(1); // Reset to first page when page size changes
                            }}
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                          
                          <Pagination>
                            <PaginationContent>
                              <PaginationItem>
                                <PaginationPrevious 
                                  href="#" 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (page > 1) setPage(page - 1);
                                  }}
                                  className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                                />
                              </PaginationItem>
                              
                              {/* First page */}
                              <PaginationItem>
                                <PaginationLink 
                                  href="#" 
                                  isActive={page === 1}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setPage(1);
                                  }}
                                >
                                  1
                                </PaginationLink>
                              </PaginationItem>
                              
                              {/* Ellipsis if needed */}
                              {page > 3 && (
                                <PaginationItem>
                                  <PaginationEllipsis />
                                </PaginationItem>
                              )}
                              
                              {/* Page before current if applicable */}
                              {page > 2 && (
                                <PaginationItem>
                                  <PaginationLink 
                                    href="#" 
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setPage(page - 1);
                                    }}
                                  >
                                    {page - 1}
                                  </PaginationLink>
                                </PaginationItem>
                              )}
                              
                              {/* Current page if not first or last */}
                              {page !== 1 && page !== totalPages && (
                                <PaginationItem>
                                  <PaginationLink 
                                    href="#" 
                                    isActive={true}
                                    onClick={(e) => e.preventDefault()}
                                  >
                                    {page}
                                  </PaginationLink>
                                </PaginationItem>
                              )}
                              
                              {/* Page after current if applicable */}
                              {page < totalPages - 1 && (
                                <PaginationItem>
                                  <PaginationLink 
                                    href="#" 
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setPage(page + 1);
                                    }}
                                  >
                                    {page + 1}
                                  </PaginationLink>
                                </PaginationItem>
                              )}
                              
                              {/* Ellipsis if needed */}
                              {page < totalPages - 2 && (
                                <PaginationItem>
                                  <PaginationEllipsis />
                                </PaginationItem>
                              )}
                              
                              {/* Last page if more than one page */}
                              {totalPages > 1 && (
                                <PaginationItem>
                                  <PaginationLink 
                                    href="#" 
                                    isActive={page === totalPages}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setPage(totalPages);
                                    }}
                                  >
                                    {totalPages}
                                  </PaginationLink>
                                </PaginationItem>
                              )}
                              
                              <PaginationItem>
                                <PaginationNext 
                                  href="#" 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (page < totalPages) setPage(page + 1);
                                  }}
                                  className={page >= totalPages ? "pointer-events-none opacity-50" : ""}
                                />
                              </PaginationItem>
                            </PaginationContent>
                          </Pagination>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </Navigation>
  );
};

export default CustomersPage;
